import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BackdropAI } from "../../../../../../AI/BackdropAI/BackdropAI";
import { useSnackbar } from "notistack";
import {
  Box,
  CardMedia,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
  alpha,
  List,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import EthiquinLoading from "../../../../../../../assets/images/Ethiquin/loadingWhite.gif";
import EthiquinAvatar from "../../../../../../../assets/images/Ethiquin/ethiquin_avatar.svg";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { TypingText } from "../../../../../../AI/TypingText/TypingText";

const suggestionAction = [
  {
    victims: [
      {
        another: [
          "Compensación económica por afectación",
          "Ajuste temporal de carga laboral",
        ],
        details:
          "La persona afectada presentó impacto directo en su desempeño y bienestar emocional derivado del incidente, por lo que se recomienda una compensación y medidas de apoyo.",
        benefits: ["monetary", "employee_benefits"],
      },
      {
        another: ["Días libres adicionales", "Acceso a apoyo psicológico"],
        details:
          "Se identificó desgaste emocional posterior al evento, haciendo necesario un periodo de recuperación y acompañamiento profesional.",
        benefits: ["employee_benefits"],
      },
      {
        another: [],
        details:
          "El análisis indica que no hubo una afectación directa medible, por lo que no se requieren acciones compensatorias adicionales.",
        benefits: ["congratulations"],
      },
      {
        another: [
          "Reconocimiento público por resiliencia",
          "Carta de agradecimiento institucional",
        ],
        details:
          "A pesar del contexto adverso, la persona mantuvo un comportamiento ejemplar y colaborativo, lo cual merece ser reconocido.",
        benefits: ["congratulations"],
      },
      {
        another: ["Entrega de equipo de trabajo actualizado"],
        details:
          "El incidente evidenció carencias materiales que influyeron indirectamente en la situación, por lo que se sugiere apoyo en especie.",
        benefits: ["in_kind"],
      },
    ],
    offenders: [
      {
        another: [
          "Advertencia formal sobre conducta",
          "Compromiso escrito de mejora",
        ],
        details:
          "La conducta identificada fue de baja gravedad pero recurrente, por lo que una retroalimentación clara puede prevenir reincidencias.",
        sanctions: ["verbal_reprimand"],
      },
      {
        another: [
          "Registro en expediente laboral",
          "Capacitación obligatoria en conducta organizacional",
        ],
        details:
          "El comportamiento tuvo impacto negativo en el equipo y requiere dejar constancia formal y reforzar habilidades interpersonales.",
        sanctions: ["documental_reprimand"],
      },
      {
        another: ["Seguimiento mensual de desempeño", "Mentoría obligatoria"],
        details:
          "Se detectaron deficiencias de liderazgo que contribuyeron al conflicto, siendo necesario un plan estructurado de mejora.",
        sanctions: ["performance_improvement_plans"],
      },
      {
        another: [
          "Cambio temporal de área",
          "Restricción de funciones de supervisión",
        ],
        details:
          "Para evitar nuevos incidentes y reducir tensiones, se recomienda una reubicación preventiva mientras se evalúa la evolución del caso.",
        sanctions: ["area_or_shift_change"],
      },
      {
        another: [
          "Inicio de proceso administrativo",
          "Separación inmediata de funciones",
        ],
        details:
          "La gravedad de los hechos y la evidencia recabada justifican la terminación de la relación laboral.",
        sanctions: ["dismisal"],
      },
    ],
  },
];

export const SanctionCompensationAISuggestion = ({
  open,
  onClose,
  title,
  involvedSelected,
  btnTitle,
  type,
  onApplySuggestion,
  options,
  t,
}) => {
  /* Refs (used for async control, animation coordination and cleanup) */
  const [legal] = useTranslation("investigation");
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useRefs
  const completedPartsRef = useRef({}); // { [index]: { primary: bool, secondary: bool } }
  const progressIntervalRef = useRef();
  const timeoutRef = useRef();
  const abortControllerRef = useRef();

  // Request lifecycle status
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'done' | 'error'
  // Raw suggestions returned by AI (or cache)
  const [suggestions, setSuggestions] = useState([]);
  // Indicates whether the data was loaded from localStorage
  const [fromCache, setFromCache] = useState(false);
  // Fake loading progress (UX only)
  const [progress, setProgress] = useState(0);
  // Index currently being animated (null = no animation)
  const [typingIndex, setTypingIndex] = useState(null);
  // Master switch to enable / disable typing animation
  const [shouldAnimate, setShouldAnimate] = useState(false);

  /* Cache configuration */
  const cacheKey = useMemo(
    () => `sanction_compensation_${involvedSelected.value}_${type}`,
    [involvedSelected, type],
  );
  // Cache validity: 24 hours
  const cacheTTL = 1000 * 60 * 60 * 24;

  /**
   * Attempts to load cached suggestions.
   * Returns null if data is missing, expired or malformed.
   */
  const loadFromCache = useCallback(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.ts || !Array.isArray(parsed.data)) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      if (Date.now() - parsed.ts > cacheTTL) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }, [cacheKey, cacheTTL]);

  /**
   * Persists AI suggestions in localStorage.
   */
  const saveToCache = useCallback(
    (data) => {
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
    },
    [cacheKey],
  );

  /**
   * Flattens and normalizes suggestions so the UI
   * can work with a single consistent structure.
   */
  const displayedSuggestions = useMemo(() => {
    if (!Array.isArray(suggestions)) return [];

    return suggestions.flatMap((container) => {
      const list =
        type === "victims"
          ? (container.victims ?? [])
          : (container.offenders ?? []);

      return list.map((item) => ({
        details: item.details ?? "",
        actions:
          type === "victims" ? (item.benefits ?? []) : (item.sanctions ?? []),
        another: item.another ?? [],
      }));
    });
  }, [suggestions, type]);

  /**
   * On modal open:
   * - If cache exists → render immediately (no animation)
   * - Otherwise → show legal notice */
  useEffect(() => {
    if (!open || suggestions.length > 0) return;

    const cached = loadFromCache();
    if (cached) {
      setFromCache(true);
      setSuggestions(cached.data);
      setStatus("done");
      setProgress(100);
      setShouldAnimate(false);
      setTypingIndex(null);
    }
  }, [open, suggestions.length, loadFromCache]);

  // open/close reset
  useEffect(() => {
    if (!open) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  }, [open]);

  // clean unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  /* Disable animation when rendering cached data */
  useEffect(() => {
    if (status === "done" && fromCache) {
      setShouldAnimate(false);
      setTypingIndex(null);
      completedPartsRef.current = {};
    }
  }, [status, fromCache]);

  /**
   * Marks a typing segment as completed and advances
   * to the next suggestion when both parts are done.
   */
  const markPartComplete = useCallback(
    (index, part) => {
      if (!completedPartsRef.current) {
        completedPartsRef.current = {};
      }

      const entry =
        completedPartsRef.current[index] ??
        (completedPartsRef.current[index] = {
          primary: false,
          secondary: false,
        });

      entry[part] = true;

      if (!displayedSuggestions[index]?.details) {
        entry.secondary = true;
      }

      if (entry.primary && entry.secondary) {
        delete completedPartsRef.current[index];
        setTypingIndex(
          index + 1 < displayedSuggestions.length ? index + 1 : null,
        );
      }
    },
    [displayedSuggestions],
  );

  const handlePrimaryComplete = useCallback(
    (index) => markPartComplete(index, "primary"),
    [markPartComplete],
  );
  const handleSecondaryComplete = useCallback(
    (index) => markPartComplete(index, "secondary"),
    [markPartComplete],
  );

  /**
   * Ejemplo de llamada "real":
   * - Reemplaza la función `fetchSuggestions` con tu fetch/fetchClient real.
   * - Usamos AbortController para poder cancelar la petición si el usuario cierra.
   */
  const fetchSuggestions = async (signal) => {
    // === EJEMPLO SIMULADO ===
    return new Promise((resolve, reject) => {
      timeoutRef.current = setTimeout(() => {
        if (signal && signal.aborted) {
          reject(new DOMException("Aborted", "AbortError"));
          return;
        }
        resolve(suggestionAction);
        timeoutRef.current = null;
      }, 2500);
    });
  };

  // Fetch suggestions
  const handleGetSuggestion = async () => {
    if (status === "loading") return;

    setFromCache(false);
    setShouldAnimate(true);
    setStatus("loading");
    setProgress(0);

    // Gradual progress increase
    progressIntervalRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + 10, 90));
    }, 300);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      const data = await fetchSuggestions(signal);
      // const data = await handleGenerate(); // llamada real
      const arr = Array.isArray(data) ? data : [];

      setSuggestions(arr);
      saveToCache(arr);
      setStatus("done");
      // Complete progress bar
      setProgress(100);
      setTypingIndex(0);
    } catch (error) {
      if (error.name === "AbortError") {
        setStatus("idle");
        console.error(error);
      } else {
        console.error("Error fetching suggestions: ", error);
        setStatus("error");
      }
    } finally {
      // Clear interval/abortcontroller
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  // Memorize a Set with the questions already present (standardized)
  const applySuggestionToForm = (suggestion) => {
    const actions =
      type === "offenders"
        ? (suggestion.actions ?? [])
        : (suggestion.actions ?? []); // actions normalizado

    const normalizedAnother = Array.isArray(suggestion.another)
      ? suggestion.another.map((text) => ({
          [type === "offenders" ? "another_sanction" : "another_benefit"]: text,
        }))
      : [];

    onApplySuggestion({
      actions,
      another: normalizedAnother,
      details: suggestion.details ?? "",
    });
  };

  const handleSelectSuggestion = (suggestion) => {
    applySuggestionToForm(suggestion);
    enqueueSnackbar(t("Resolution.suggestion_applied"), {
      variant: "success",
    });
    onClose();
  };

  // helper to extract plain text from React nodes
  const extractPlainText = (node) => {
    if (node == null || node === true || node === false) return "";
    if (typeof node === "string" || typeof node === "number")
      return String(node);
    if (Array.isArray(node)) return node.map(extractPlainText).join("");
    if (React.isValidElement(node))
      return extractPlainText(node.props.children);
    return "";
  };

  // Reset state on close
  const resetState = useCallback(() => {
    setFromCache(false);
    setSuggestions([]);
    setStatus("idle");
    setProgress(0);
    setTypingIndex(null);
    setShouldAnimate(false);
    completedPartsRef.current = {};

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const optionsMap = useMemo(() => {
    const map = new Map();
    (options || []).forEach((opt) => {
      map.set(opt.value, opt.label);
    });
    return map;
  }, [options]);

  const resolveActionLabel = useCallback(
    (value) => optionsMap.get(value) ?? value,
    [optionsMap],
  );

  return (
    <BackdropAI
      open={open}
      onClose={onClose}
      onExited={resetState}
      title={title}
    >
      {status === "loading" ? (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 64px)",
            flexFlow: "column",
          }}
        >
          <Box
            role="status"
            aria-live="polite"
            sx={{ width: "100%", textAlign: "center" }}
          >
            <CardMedia
              component="img"
              src={EthiquinLoading}
              alt={t("Loading Suggestions")}
              sx={{
                width: { xs: "60%", md: "40%" },
                height: "auto",
                mx: "auto",
                my: 2.5,
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
            {/* Progress bar */}
            <Box
              sx={{
                width: { xs: "90%", md: "70%" },
                paddingY: 1,
                mx: "auto",
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e3f2fd",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                  },
                }}
                aria-label={t("Loading suggestions")}
              />
            </Box>
          </Box>
        </Stack>
      ) : suggestions.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <CardMedia
            component="img"
            src={EthiquinAvatar}
            alt={"loading..."}
            sx={{
              border: `2px solid ${theme.palette.primary.dark}`,
              borderRadius: "50%",
              width: "20%",
              height: "auto",
              mx: "auto",
              userSelect: "none",
            }}
          />
          <Typography
            color={"warning.dark"}
            textAlign="justify"
            sx={{
              borderLeft: `5px solid ${theme.palette.warning.main}`,
              backgroundColor: alpha(theme.palette.warning.light, 0.1),
              boxShadow: `0 0 10px -4px ${theme.palette.warning.dark}`,
              borderRadius: 1,
              py: 2,
              px: 3,
              mx: 3,
              my: 5,
              fontSize: 15,
            }}
          >
            <strong>{legal("legalNotice.legal_notice")}: </strong>
            <Typography
              fontWeight={500}
              dangerouslySetInnerHTML={{
                __html: legal("legalNotice.AI_generated_questions"),
              }}
              component={"span"}
            />
          </Typography>
          <EGButton
            label={btnTitle}
            color="primary"
            iconName="outline_ai"
            onClick={handleGetSuggestion}
          />
        </Box>
      ) : (
        // Suggestion list
        <Box sx={{ px: 2 }}>
          <Typography mt={1.5} variant="h6">
            {`${t("Resolution.select_an_option")}: `}
          </Typography>

          <List>
            {displayedSuggestions.map((s, i) => {
              const visible =
                typingIndex === null
                  ? true
                  : typingIndex === -1
                    ? false
                    : i <= typingIndex;

              const primaryText = (
                <Stack sx={{ flexFlow: "column" }}>
                  <Typography fontWeight={600} component="span" fontSize={14.5}>
                    {`${t("Resolution.details")}: `}
                  </Typography>

                  <Typography
                    component="span"
                    fontSize={14}
                    fontWeight={600}
                    textAlign="justify"
                    px={0.75}
                  >
                    {s.details}
                  </Typography>
                </Stack>
              );

              const secondaryText = (
                <Stack>
                  {Array.isArray(s.actions) && s.actions.length > 0 && (
                    <Stack
                      my={0.75}
                      sx={{
                        borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                      }}
                    >
                      <Typography
                        mt={0.5}
                        fontWeight={600}
                        component="span"
                        fontSize={12.5}
                      >
                        {`${t("Resolution.actions_to_be_taken")}: `}
                      </Typography>

                      {s.actions.map((action, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          component="span"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          • {resolveActionLabel(action)}
                        </Typography>
                      ))}
                      {s.another.map((another, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          component="span"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          • {another}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              );

              const primaryPlain = extractPlainText(primaryText);
              const secondaryPlain = extractPlainText(secondaryText);

              return (
                <Collapse
                  key={i}
                  in={visible}
                  timeout={180}
                  mountOnEnter
                  unmountOnExit
                >
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        handleSelectSuggestion(s);
                      }}
                      dense
                      sx={{
                        border: `1px solid ${theme.palette.blueGray50.main}`,
                        backgroundColor: "transparent",
                        borderRadius: 3,
                        boxShadow: `0 0 6px -4px ${theme.palette.common.black}`,
                      }}
                    >
                      <ListItemIcon>
                        <EGIconButton
                          iconName="add_square"
                          iconColor="success"
                          iconColorHover="white"
                          colorHover="success"
                          title={t("Interview.add")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectSuggestion(s);
                          }}
                        />
                      </ListItemIcon>

                      <ListItemText
                        disableTypography
                        primary={
                          shouldAnimate && typingIndex === i ? (
                            <TypingText
                              text={primaryPlain}
                              speed={2}
                              active={shouldAnimate && typingIndex === i}
                              onComplete={() => handlePrimaryComplete(i)}
                              startEmpty={shouldAnimate}
                            />
                          ) : (
                            primaryText
                          )
                        }
                        primaryTypographyProps={{
                          fontWeight: 600,
                          variant: "body1",
                          whiteSpace: "pre-line",
                        }}
                        secondary={
                          shouldAnimate && typingIndex === i ? (
                            <TypingText
                              text={secondaryPlain}
                              speed={2}
                              active={shouldAnimate && typingIndex === i}
                              onComplete={() => handleSecondaryComplete(i)}
                              startEmpty={true}
                            />
                          ) : (
                            secondaryText
                          )
                        }
                        secondaryTypographyProps={{
                          component: "div",
                          variant: "body1",
                          fontSize: "0.83rem",
                          whiteSpace: "pre-line",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </Collapse>
              );
            })}
          </List>
        </Box>
      )}
    </BackdropAI>
  );
};
